// src/services/applicationService.js
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../authorization/authService";

export const applicationApi = createApi({
  reducerPath: "applicationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Application",
    "SelfDeclarationTemplate",
    "EligibilityValue",
    "FileDownload",
    "MirroredFiles",
  ],
  endpoints: (builder) => ({
    getApplications: builder.query({
      query: (filters) => ({
        url: "/application/list",
        method: "GET",
        params: filters,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Application", id })),
              { type: "Application", id: "LIST" },
            ]
          : [{ type: "Application", id: "LIST" }],
    }),

    getSelfDeclarationTemplates: builder.query({
      query: (applicationId) => ({
        url: "/application/self_declaration_templates",
        method: "GET",
        params: applicationId ? { application_id: applicationId } : {},
      }),
      providesTags: (result, error, applicationId) => [
        "SelfDeclarationTemplate",
        { type: "SelfDeclarationTemplate", id: applicationId || "ALL" },
      ],
    }),

    getMirroredFilesForTemplate: builder.query({
      query: ({ templateId, applicationId }) => ({
        url: `/application/self_declaration_template/${templateId}/mirrored-files`,
        method: "GET",
        params: { application_id: applicationId },
      }),
      providesTags: (result, error, { templateId, applicationId }) => [
        {
          type: "MirroredFiles",
          id: `${templateId}-${applicationId}`,
        },
        { type: "MirroredFiles", id: "ALL" },
        { type: "MirroredFiles", id: `ALL-${applicationId}` }, // Specific app targeting
        "File",
      ],
    }),

    uploadFileToTemplate: builder.mutation({
      query: ({ templateId, applicationId, file, comment }) => {
        const formData = new FormData();
        formData.append("application_id", applicationId);
        formData.append("file", file);
        if (comment) {
          formData.append("comment", comment);
        }

        return {
          url: `/application/self_declaration_template/${templateId}/upload`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { templateId, applicationId }) => [
        { type: "MirroredFiles", id: `${templateId}-${applicationId}` },
        { type: "MirroredFiles", id: `${templateId}-ALL` },
        { type: "MirroredFiles", id: "ALL" },
        { type: "MirroredFiles", id: `ALL-${applicationId}` },
        { type: "File", applicationId },
        "File",
        "SelfDeclarationTemplate",
      ],
      async onQueryStarted(
        { templateId, applicationId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: result } = await queryFulfilled;
          // Optimistic update: add file to embedded mirrored_application_files in templates cache
          dispatch(
            applicationApi.util.updateQueryData(
              "getSelfDeclarationTemplates",
              applicationId,
              (draft) => {
                const template = draft.data?.find((t) => t.id === templateId);
                if (template) {
                  template.mirrored_application_files = [
                    ...(template.mirrored_application_files ?? []),
                    result.data,
                  ];
                }
              }
            )
          );
        } catch {
          // Tag invalidation (invalidatesTags) handles eventual consistency as fallback
        }
      },
    }),

    uploadFileForSelfDeclarationTemplate: builder.mutation({
      query: ({ templateId, file }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: `/application/self_declaration_template/${templateId}/upload-file`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { templateId }) => [
        "SelfDeclarationTemplate",
        { type: "SelfDeclarationTemplate", id: "ALL" },
        { type: "MirroredFiles", id: `${templateId}-ALL` },
      ],
    }),

    deleteFileForSelfDeclarationTemplate: builder.mutation({
      query: ({ templateId }) => ({
        url: `/application/self_declaration_template/${templateId}/file-only`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { templateId }) => [
        "SelfDeclarationTemplate",
        { type: "SelfDeclarationTemplate", id: "ALL" },
        { type: "MirroredFiles", id: `${templateId}-ALL` },
        { type: "MirroredFiles", id: "ALL" },
        "File",
      ],
    }),

    getNotExcludedApplications: builder.query({
      query: ({ call_id }) => ({
        url: `application/${call_id}/to_be_rewarded_applications`,
        method: "GET",
      }),
      providesTags: (result, error, { call_id }) => {
        if (!result || !result.data) {
          return [{ type: "Application", id: `CALL_${call_id}` }];
        }

        return [
          ...[result.data]
            .flat()
            .map(({ id }) => ({ type: "Application", id })),
          { type: "Application", id: `CALL_${call_id}` },
        ];
      },
    }),

    getEligibilityValues: builder.query({
      query: () => ({
        url: "/application/eligibility",
        method: "GET",
      }),
      providesTags: ["EligibilityValue"],
    }),

    createApplicationWithSelfDeclaration: builder.mutation({
      query: (data) => ({
        url: "/application/full",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Application"],
    }),

    returnToBank: builder.mutation({
      query: ({ id, data }) => ({
        url: `/application/return_to_bank/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Application", id }],
    }),
    excludeApplication: builder.mutation({
      query: ({ id, place }) => ({
        url: `/application/${id}/exclude?place=${place}`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Application", id }],
    }),

    submitStatusApplication: builder.mutation({
      query: ({ id }) => ({
        url: `/application/${id}/submit`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Application", id }],
    }),

    validateApplication: builder.mutation({
      query: ({ id }) => ({
        url: `/application/${id}/validate`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Application", id }],
    }),

    updateApplication: builder.mutation({
      query: ({ id, data }) => ({
        url: `/application/update/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Application", id }],
    }),

    deleteApplication: builder.mutation({
      query: (id) => ({
        url: `/application/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Application", id }],
    }),

    getApplicationById: builder.query({
      query: (id) => ({
        url: `/application/detail/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Application", id }],
    }),

    getSubmittedApplications: builder.query({
      query: ({ show_all }) => ({
        url: "/application/submitted_applications",
        method: "GET",
        params: { show_all },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Application", id })),
              { type: "Application", id: "LIST" },
            ]
          : [{ type: "Application", id: "LIST" }],
    }),

    getParticipationInOtherCalls: builder.query({
      query: ({ bank_id, call_application_id }) => ({
        url: `/application/${bank_id}/${call_application_id}/list`,
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "Application", id })),
              { type: "Application", id: "LIST" },
            ]
          : [{ type: "Application", id: "LIST" }],
    }),

    // New endpoint for analytics statuses
    getApplicationAnalyticsStatuses: builder.query({
      query: () => ({
        url: "/application/analytics/statuses",
        method: "GET",
      }),
      providesTags: [{ type: "AnalyticsStatuses", id: "LIST" }],
    }),

    downloadSelfDeclarationFile: builder.query({
      query: (templateId) => ({
        url: `/application/self_declaration_templates/download/${templateId}`,
        method: "GET",
        responseHandler: (response) => response.blob(), // Return the file blob
      }),
      providesTags: (result, error, templateId) => [
        { type: "FileDownload", id: templateId },
      ],
    }),

    disburseApplication: builder.mutation({
      query: (id) => ({
        url: `/application/${id}/disburse`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Application", id },
        { type: "Application", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useGetSelfDeclarationTemplatesQuery,
  useGetMirroredFilesForTemplateQuery,
  useDownloadSelfDeclarationFileQuery,
  useGetEligibilityValuesQuery,
  useCreateApplicationWithSelfDeclarationMutation,
  useGetNotExcludedApplicationsQuery,
  useUpdateApplicationMutation,
  useValidateApplicationMutation,
  useSubmitStatusApplicationMutation,
  useDeleteApplicationMutation,
  useGetApplicationByIdQuery,
  useGetSubmittedApplicationsQuery,
  useReturnToBankMutation,
  useExcludeApplicationMutation,
  useGetParticipationInOtherCallsQuery,
  useGetApplicationAnalyticsStatusesQuery,
  useUploadFileToTemplateMutation,
  useUploadFileForSelfDeclarationTemplateMutation,
  useDeleteFileForSelfDeclarationTemplateMutation,
  useDisburseApplicationMutation,
} = applicationApi;
